import { useState, useEffect, useRef } from "react"
import { Loader2, User, Building2, ShieldCheck, Download, Clock, XCircle, FileImage, Check } from "lucide-react"
import SwimmerSidebar from "../components/SwimmerSidebar"
import { QRCodeSVG } from "qrcode.react"
import QRCode from "qrcode"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"
const DATA_API = API

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(",")[1])
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function generateIdCode(id) {
  if (!id) return "VAS-000000"
  const hex = id.replace(/[^a-f0-9]/gi, "").toUpperCase()
  return `VAS-${hex.slice(0, 6)}`
}

function formatDob(dob) {
  if (!dob) return "—"
  
  // If it's already in DD/MM/YYYY format from the signup form
  if (typeof dob === "string" && dob.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = dob.split("/");
    const d = new Date(`${year}-${month}-${day}T00:00:00Z`);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    }
    return dob; // fallback to raw string if parsing fails
  }

  try {
    const d = new Date(dob);
    if (isNaN(d.getTime())) return dob;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  } catch {
    return dob
  }
}

export default function SwimmerProfile() {
  const [profile, setProfile] = useState(null)
  const [clubName, setClubName] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [photoUrl, setPhotoUrl] = useState(null)
  const cardRef = useRef(null)
  const rotatingRef = useRef(null)
  const frontRef = useRef(null)
  const backRef = useRef(null)
  const [isFlipped, setIsFlipped] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const { t } = useLanguage()

  // ── helpers used by the canvas-draw download ──────────────────────────────
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r)
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h)
    ctx.quadraticCurveTo(x, y + h, x, y + h - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
  }

  /** Draws text that wraps to maxLines lines within maxWidth, returns line count */
  function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    const words = text.split(" ")
    let line = ""
    let linesDrawn = 0
    for (let i = 0; i < words.length; i++) {
      const test = line ? line + " " + words[i] : words[i]
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, y + linesDrawn * lineHeight)
        linesDrawn++
        if (linesDrawn >= maxLines) return linesDrawn
        line = words[i]
      } else {
        line = test
      }
    }
    if (line) {
      ctx.fillText(line, x, y + linesDrawn * lineHeight)
      linesDrawn++
    }
    return linesDrawn
  }

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    setClubName(localStorage.getItem("clubName") || "")
    async function fetchProfile() {
      setIsLoading(true)
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch profile")
        const data = await res.json()
        setProfile(data)
        if (data.club_name) {
          setClubName(data.club_name)
          localStorage.setItem("clubName", data.club_name)
        }
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchProfile()
  }, [])

  useEffect(() => {
    if (!profile?.has_photo) return
    fetch(`${API}/api/me/photo`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.photo_base64) setPhotoUrl(`data:image/png;base64,${data.photo_base64}`) })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.has_photo])

  const idCode = profile ? generateIdCode(profile.id) : "VAS-——"
  const initials = profile?.name ? profile.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?"

  async function handleDownload() {
    setDownloading(true)
    try {
      const { jsPDF } = await import("jspdf")

      // ── Card dimensions (fixed, independent of screen) ────────────────────
      const W = 800, H = 512   // landscape credit-card ratio
      const R = 24             // corner radius
      const PAD = 36           // outer padding

      // ── Load assets ───────────────────────────────────────────────────────
      const [bgFront, bgBack, logo, photo] = await Promise.all([
        loadImage("/id_front_bg.png").catch(() => null),
        loadImage("/id_back_bg.png").catch(() => null),
        loadImage("/vietnam_aquatics.png").catch(() => null),
        photoUrl ? loadImage(photoUrl).catch(() => null) : Promise.resolve(null),
      ])

      // ══════════════════════════════════════════════════════════════════════
      // FRONT SIDE
      // ══════════════════════════════════════════════════════════════════════
      const frontCanvas = document.createElement("canvas")
      frontCanvas.width = W
      frontCanvas.height = H
      const fc = frontCanvas.getContext("2d")

      // Clip to rounded rect so nothing ever escapes the card boundary
      roundRect(fc, 0, 0, W, H, R)
      fc.clip()

      // Background
      fc.fillStyle = "#FFFFFF"
      fc.fillRect(0, 0, W, H)
      if (bgFront) fc.drawImage(bgFront, 0, 0, W, H)

      // Logo (top-left)
      const LOGO_SIZE = 72
      if (logo) fc.drawImage(logo, PAD, PAD, LOGO_SIZE, LOGO_SIZE)

      // ── Photo box ─────────────────────────────────────────────────────────
      const photoAreaTop = 130
      const photoH = H - photoAreaTop - PAD   // extends to bottom with padding
      const photoW = Math.round(photoH * 0.81)
      const photoX = PAD
      const photoY = photoAreaTop

      // Clip photo to rounded rect
      fc.save()
      roundRect(fc, photoX, photoY, photoW, photoH, 12)
      fc.clip()
      if (photo) {
        fc.drawImage(photo, photoX, photoY, photoW, photoH)
      } else {
        fc.fillStyle = "rgba(0,163,224,0.1)"
        fc.fillRect(photoX, photoY, photoW, photoH)
        // Initials
        fc.fillStyle = "#007CA8"
        fc.font = "bold 64px sans-serif"
        fc.textAlign = "center"
        fc.textBaseline = "middle"
        fc.fillText(initials, photoX + photoW / 2, photoY + photoH / 2)
      }
      fc.restore()

      // ── Info area: fully bottom-up so content clusters in lower half ──────
      const infoX = PAD + photoW + 24
      const infoW = W - infoX - PAD
      const LINE_H = 28   // row height for each data row

      fc.textAlign = "left"
      fc.textBaseline = "alphabetic"

      let cursorY = H - PAD   // start at bottom anchor (y = 476)

      // ── SWIMMER ID (bottom row) ──────────────────────────────────────────
      fc.fillStyle = "rgba(15,34,64,0.5)"
      fc.font = "bold 13px sans-serif"
      fc.fillText("SWIMMER ID", infoX, cursorY)
      fc.fillStyle = "#0F2240"
      fc.font = "bold 16px monospace"
      fc.textAlign = "right"
      fc.fillText(idCode, infoX + infoW, cursorY)
      cursorY -= LINE_H   // → 448

      // ── DOB row ──────────────────────────────────────────────────────────
      fc.textAlign = "left"
      fc.fillStyle = "rgba(15,34,64,0.5)"
      fc.font = "bold 13px sans-serif"
      fc.fillText("DOB", infoX, cursorY)
      fc.fillStyle = "#0F2240"
      fc.font = "bold 16px sans-serif"
      fc.textAlign = "right"
      fc.fillText(formatDob(profile.dob), infoX + infoW, cursorY)
      cursorY -= LINE_H   // → 420

      // ── CLUB row (wraps to 2 lines if needed) ────────────────────────────
      const clubText = clubName || "Unaffiliated"
      fc.font = "bold 16px sans-serif"
      const maxClubW = infoW - 52
      const clubWords = clubText.split(" ")
      let cLine1 = "", cLine2 = ""
      for (const w of clubWords) {
        const test = cLine1 ? cLine1 + " " + w : w
        if (fc.measureText(test).width <= maxClubW) cLine1 = test
        else cLine2 = (cLine2 ? cLine2 + " " : "") + w
      }
      if (cLine2) {
        fc.fillStyle = "#0F2240"
        fc.textAlign = "right"
        fc.fillText(cLine2, infoX + infoW, cursorY)
        cursorY -= 22
        fc.fillText(cLine1, infoX + infoW, cursorY)
        fc.textAlign = "left"
        fc.fillStyle = "rgba(15,34,64,0.5)"
        fc.font = "bold 13px sans-serif"
        fc.fillText("CLUB", infoX, cursorY)
        cursorY -= LINE_H
      } else {
        fc.fillStyle = "#0F2240"
        fc.textAlign = "right"
        fc.fillText(cLine1, infoX + infoW, cursorY)
        fc.textAlign = "left"
        fc.fillStyle = "rgba(15,34,64,0.5)"
        fc.font = "bold 13px sans-serif"
        fc.fillText("CLUB", infoX, cursorY)
        cursorY -= LINE_H   // → 392
      }

      // ── Divider ──────────────────────────────────────────────────────────
      cursorY -= 8   // gap above divider
      fc.strokeStyle = "rgba(0,163,224,0.3)"
      fc.lineWidth = 1
      fc.beginPath()
      fc.moveTo(infoX, cursorY)
      fc.lineTo(infoX + infoW, cursorY)
      fc.stroke()
      cursorY -= 20   // gap below divider → now at ~364

      // ── Swimmer name (up to 2 lines, drawn bottom-up above divider) ──────
      fc.fillStyle = "#0F2240"
      fc.font = "900 28px sans-serif"
      fc.textAlign = "left"
      const nameUpper = (profile.name || "").toUpperCase()
      const nameWords = nameUpper.split(" ")
      let nameLine1 = "", nameLine2 = ""
      for (const w of nameWords) {
        const test = nameLine1 ? nameLine1 + " " + w : w
        if (fc.measureText(test).width <= infoW) nameLine1 = test
        else nameLine2 = (nameLine2 ? nameLine2 + " " : "") + w
      }
      if (nameLine2) {
        fc.fillText(nameLine2, infoX, cursorY)
        cursorY -= 36
        fc.fillText(nameLine1, infoX, cursorY)
      } else {
        fc.fillText(nameLine1, infoX, cursorY)
      }
      cursorY -= 36   // space between name and SWIMMER label

      // ── "SWIMMER" label ──────────────────────────────────────────────────
      fc.fillStyle = "#007CA8"
      fc.font = "800 13px sans-serif"
      fc.letterSpacing = "2px"
      fc.fillText("SWIMMER", infoX, cursorY)
      fc.letterSpacing = "0px"

      // ══════════════════════════════════════════════════════════════════════
      // BACK SIDE
      // ══════════════════════════════════════════════════════════════════════
      const backCanvas = document.createElement("canvas")
      backCanvas.width = W
      backCanvas.height = H
      const bc = backCanvas.getContext("2d")

      roundRect(bc, 0, 0, W, H, R)
      bc.clip()

      bc.fillStyle = "#0A1128"
      bc.fillRect(0, 0, W, H)
      if (bgBack) bc.drawImage(bgBack, 0, 0, W, H)

      // Border
      bc.strokeStyle = "rgba(255,255,255,0.1)"
      bc.lineWidth = 1
      roundRect(bc, 0.5, 0.5, W - 1, H - 1, R)
      bc.stroke()

      // "AUTHORIZED ACCESS"
      bc.fillStyle = "#60A5FA"
      bc.font = "bold 22px sans-serif"
      bc.textAlign = "left"
      bc.textBaseline = "alphabetic"
      bc.fillText("AUTHORIZED ACCESS", PAD + 32, PAD + 26)

      // Body text
      bc.fillStyle = "rgba(248,250,252,0.6)"
      bc.font = "14px sans-serif"
      const bodyText = "This credential is the property of Viet Nam Aquatics. It must be presented upon request and surrendered upon expiration or revocation."
      drawWrappedText(bc, bodyText, PAD, PAD + 56, W - PAD * 2, 20, 4)

      // Bottom divider
      bc.strokeStyle = "rgba(255,255,255,0.1)"
      bc.lineWidth = 1
      bc.beginPath()
      bc.moveTo(PAD, H - PAD - 70)
      bc.lineTo(W - PAD, H - PAD - 70)
      bc.stroke()

      // Logo + Issuer
      if (logo) bc.drawImage(logo, PAD, H - PAD - 56, 48, 48)
      bc.fillStyle = "rgba(255,255,255,0.5)"
      bc.font = "bold 11px sans-serif"
      bc.textAlign = "left"
      bc.fillText("ISSUER", PAD + 60, H - PAD - 38)
      bc.fillStyle = "#FFFFFF"
      bc.font = "bold 16px sans-serif"
      bc.fillText("VIET NAM AQUATICS", PAD + 60, H - PAD - 18)

      // QR code (generate real QR using qrcode package)
      const qrSize = 70
      const qrX = W - PAD - qrSize
      const qrY = H - PAD - qrSize
      bc.fillStyle = "#FFFFFF"
      roundRect(bc, qrX - 6, qrY - 6, qrSize + 12, qrSize + 12, 8)
      bc.fill()
      
      try {
        const qrDataUrl = await QRCode.toDataURL(idCode, {
          width: qrSize,
          margin: 0,
          color: { dark: "#0F2240", light: "#FFFFFF" }
        })
        const qrImage = await loadImage(qrDataUrl)
        bc.drawImage(qrImage, qrX, qrY, qrSize, qrSize)
      } catch (err) {
        console.error("Failed to generate QR code", err)
      }

      // ── Build PDF ─────────────────────────────────────────────────────────
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [W, H] })
      pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, W, H)
      pdf.addPage([W, H], "landscape")
      pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, W, H)
      pdf.save(`${(profile.name || "swimmer").replace(/\s+/g, "-").toLowerCase()}-id-card.pdf`)
    } catch (err) {
      console.error("PDF generation failed:", err)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row lg:h-screen lg:overflow-hidden">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-10">
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("profile_title")}</h1>
          <p className="mt-1 text-sm text-mist-300">{t("profile_subtitle")}</p>
        </header>

        {error && (
          <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
          </div>
        ) : profile ? (
          <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-start">

            {/* ── ID Card ── */}
            <div className="flex flex-col items-center shrink-0 w-full max-w-[500px] lg:w-[500px] lg:sticky lg:top-10 lg:self-start">
              <div
                ref={cardRef}
                className="relative w-full aspect-[1/0.64] cursor-pointer group"
                style={{ perspective: "1200px" }}
                onClick={() => setIsFlipped(!isFlipped)}
              >
                <div
                  ref={rotatingRef}
                  className="relative w-full h-full transition-transform duration-700 ease-in-out"
                  style={{ transformStyle: "preserve-3d", transform: isFlipped ? "rotateY(180deg)" : "rotateY(0)" }}
                >

                  {/* ── Front Side ── */}
                  <div
                    ref={frontRef}
                    className="absolute inset-0 w-full h-full overflow-hidden rounded-2xl shadow-2xl bg-white"
                    style={{
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      backgroundImage: "url(/id_front_bg.png)",
                      backgroundSize: "100% 100%", // matches resizeMode: stretch
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat"
                    }}
                  >
                    {/* Glass Border */}
                    <div className="absolute inset-0 rounded-2xl border border-white/60 pointer-events-none" />
                    
                    {/* Top Logo */}
                    <div className="absolute top-4 left-4">
                      <img src="/vietnam_aquatics.png" alt="Logo" className="w-[60px] h-[60px] object-contain" />
                    </div>

                    {/* Front Content */}
                    <div className="absolute inset-x-0 bottom-0 top-[60px] flex flex-row items-end px-5 pb-6 gap-4">
                      
                      {/* Photo Area (Left) */}
                      <div className="relative rounded-xl overflow-hidden flex items-center justify-center shrink-0 border border-white/80 h-[55%] aspect-[0.81]"
                           style={{ backgroundColor: "rgba(0,163,224,0.1)" }}>
                        {photoUrl ? (
                          <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-white/50">
                            <span className="font-black text-[36px]" style={{ color: "#007CA8" }}>{initials}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-white/10 pointer-events-none" />
                      </div>

                      {/* Info Area (Right) */}
                      <div className="flex-1 flex flex-col justify-end pb-1 h-full overflow-hidden">
                        <p className="text-[12px] font-extrabold tracking-[1.5px] mb-1" style={{ color: "#007CA8" }}>SWIMMER</p>
                        <p className="text-[22px] font-black leading-[24px] tracking-[-0.5px] uppercase" style={{ color: "#0F2240", wordBreak: "break-word", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {profile.name}
                        </p>
                        
                        <div className="w-full h-px my-2" style={{ backgroundColor: "rgba(0,163,224,0.2)" }} />

                        <div className="flex flex-row items-start justify-between mb-1 gap-2">
                          <p className="text-[11px] font-bold shrink-0" style={{ color: "rgba(15, 34, 64, 0.6)" }}>CLUB</p>
                          <p className="text-[13px] font-extrabold text-right" style={{ color: "#0F2240", wordBreak: "break-word", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{clubName || "Unaffiliated"}</p>
                        </div>

                        <div className="flex flex-row items-center justify-between mb-1">
                          <p className="text-[11px] font-bold" style={{ color: "rgba(15, 34, 64, 0.6)" }}>DOB</p>
                          <p className="text-[14px] font-extrabold" style={{ color: "#0F2240" }}>{formatDob(profile.dob)}</p>
                        </div>
                        
                        {/* ID Number kept per user request */}
                        <div className="flex flex-row items-center justify-between">
                          <p className="text-[11px] font-bold" style={{ color: "rgba(15, 34, 64, 0.6)" }}>SWIMMER ID</p>
                          <p className="text-[14px] font-extrabold font-mono uppercase" style={{ color: "#0F2240" }}>{idCode}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Back Side ── */}
                  <div
                    ref={backRef}
                    className="absolute inset-0 w-full h-full overflow-hidden rounded-2xl shadow-2xl"
                    style={{
                      backgroundColor: "#0A1128",
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                      backgroundImage: "url(/id_back_bg.png)",
                      backgroundSize: "100% 100%", // matches resizeMode: stretch
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat"
                    }}
                  >
                    {/* Glass Border */}
                    <div className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none" />

                    <div className="flex flex-col p-6 h-full justify-center">
                      <div className="flex flex-row items-center gap-2 mb-3">
                        <ShieldCheck className="h-6 w-6" style={{ color: "#60A5FA" }} />
                        <p className="text-[16px] font-extrabold tracking-[1.5px]" style={{ color: "#F8FAFC" }}>AUTHORIZED ACCESS</p>
                      </div>

                      <p className="text-[12px] font-medium leading-[18px]" style={{ color: "rgba(248,250,252,0.6)" }}>
                        This credential is the property of Viet Nam Aquatics. It must be presented upon request and surrendered upon expiration or revocation.
                      </p>

                      <div className="flex flex-row items-center justify-between mt-auto pt-4 border-t border-white/10">
                        <div className="flex flex-row items-center gap-3">
                          <img src="/vietnam_aquatics.png" alt="Logo" className="w-[42px] h-[42px] opacity-80 object-contain" />
                          <div>
                            <p className="text-[12px] font-extrabold tracking-[1.5px] mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>ISSUER</p>
                            <p className="text-[14px] font-extrabold text-white">VIET NAM AQUATICS</p>
                          </div>
                        </div>
                        
                        <div className="bg-white p-1 rounded-lg">
                          <QRCodeSVG value={idCode} size={46} level="M" fgColor="#0F2240" bgColor="#FFFFFF" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-[12px] font-semibold tracking-[0.5px]" style={{ color: "#94A3B8" }}>{t("profile_tap_flip")}</p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="mt-4 flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-paper-0 transition-colors hover:border-gold-500/40 hover:text-gold-300 disabled:opacity-50"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {t("profile_download")}
              </button>
            </div>

            {/* ── Profile Details Panel ── */}
            <div className="flex-1 space-y-5">
              <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6">
                <h2 className="font-display text-lg font-bold text-paper-0 mb-5 flex items-center gap-2">
                  <User className="h-5 w-5 text-gold-400" /> {t("profile_personal")}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <InfoRow label={t("profile_full_name")} value={profile.name} />
                  <InfoRow label={t("profile_email")} value={profile.email} />
                  <InfoRow label={t("profile_phone")} value={profile.phone || t("common_na")} />
                  <InfoRow label={t("profile_dob")} value={formatDob(profile.dob)} />
                  <InfoRow label={t("profile_blood")} value={profile.blood || t("common_na")} />
                  <InfoRow label={t("profile_role")} value={t("profile_role_swimmer")} accent />
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6">
                <h2 className="font-display text-lg font-bold text-paper-0 mb-5 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gold-400" /> {t("profile_club_coaching")}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <InfoRow label={t("profile_club")} value={clubName || t("profile_unaffiliated")} />
                  <InfoRow label={t("profile_coach")} value={profile.coach_name || t("common_na")} />
                  <InfoRow label={t("profile_status")} value={profile.status || "active"} accent capitalize />
                  <InfoRow label={t("profile_swimmer_id")} value={idCode} mono />
                </div>
              </div>

              <div className="rounded-xl border border-gold-500/10 bg-gold-500/5 px-6 py-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-gold-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gold-300">{t("profile_official_id")}</p>
                  <p className="text-xs text-mist-400 mt-0.5">{t("profile_official_desc")}</p>
                </div>
              </div>

              {(profile.status === "pending" || profile.status === "rejected") && (
                <VerificationPanel
                  status={profile.status}
                  photoUrl={photoUrl || (profile.has_photo ? "pending" : null)}
                  birthCertStatus={profile.birth_cert_status}
                  birthCertRejectReason={profile.birth_cert_reject_reason}
                />
              )}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  )
}

function VerificationPanel({ status, photoUrl, birthCertStatus, birthCertRejectReason }) {
  const [certFile, setCertFile] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadMsg, setUploadMsg] = useState("")
  const { t } = useLanguage()

  // "Cleared" = already submitted and not needing re-upload — pending review
  // or already approved. Only missing/rejected documents get asked for again.
  const certCleared = birthCertStatus === "pending" || birthCertStatus === "approved"
  const photoCleared = !!photoUrl
  const bothCleared = certCleared && photoCleared

  const [viewOpen, setViewOpen] = useState(null) // null | "cert" | "photo"
  const [certView, setCertView] = useState(null) // { src, isPdf }
  const [certViewLoading, setCertViewLoading] = useState(false)
  const [certViewError, setCertViewError] = useState("")

  const [clubs, setClubs] = useState([])
  const [coaches, setCoaches] = useState([])
  const [clubId, setClubId] = useState("")
  const [coachId, setCoachId] = useState("")
  const [resubmitting, setResubmitting] = useState(false)
  const [resubmitMsg, setResubmitMsg] = useState("")

  function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    if (status !== "rejected") return
    fetch(`${DATA_API}/api/clubs`).then(r => r.json()).then(setClubs).catch(() => {})
  }, [status])

  useEffect(() => {
    if (!clubId) { setCoaches([]); return }
    fetch(`${DATA_API}/api/clubs/${clubId}/coaches`).then(r => r.json()).then(setCoaches).catch(() => {})
  }, [clubId])

  const handleUpload = async (e) => {
    e.preventDefault()
    setUploadMsg("")
    setUploading(true)
    try {
      if (certFile) {
        const base64 = await fileToBase64(certFile)
        const res = await fetch(`${DATA_API}/api/me/birth-certificate`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({ birth_certificate_base64: base64, content_type: certFile.type }),
        })
        if (!res.ok) throw new Error("Birth certificate upload failed.")
      }
      if (photoFile) {
        const base64 = await fileToBase64(photoFile)
        const res = await fetch(`${DATA_API}/api/me/photo`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({ photo_base64: base64 }),
        })
        if (!res.ok) throw new Error("Profile photo upload failed.")
      }
      if (!certFile && !photoFile) { setUploading(false); return }
      setUploadMsg("Uploaded. Your documents are pending review.")
      setCertFile(null)
      setPhotoFile(null)
    } catch (err) {
      setUploadMsg(err.message)
    } finally {
      setUploading(false)
    }
  }

  const toggleViewCert = async () => {
    if (viewOpen === "cert") { setViewOpen(null); return }
    setViewOpen("cert")
    if (certView) return
    setCertViewError("")
    setCertViewLoading(true)
    try {
      const res = await fetch(`${DATA_API}/api/me/birth-certificate`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to load birth certificate")
      const data = await res.json()
      const contentType = data.content_type || "image/png"
      setCertView({ src: `data:${contentType};base64,${data.birth_certificate_base64}`, isPdf: contentType === "application/pdf" })
    } catch (err) {
      setCertViewError(err.message)
    } finally {
      setCertViewLoading(false)
    }
  }

  const handleResubmit = async (e) => {
    e.preventDefault()
    setResubmitMsg("")
    if (!clubId || !coachId) { setResubmitMsg("Please select a club and coach."); return }
    setResubmitting(true)
    try {
      const res = await fetch(`${DATA_API}/api/me/resubmit-membership`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ club_id: clubId, coach_id: coachId }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || "Could not resubmit your membership request.")
      }
      const club = clubs.find(c => c.id === clubId)
      if (club) localStorage.setItem("clubName", club.name)
      window.location.reload()
    } catch (err) {
      setResubmitMsg(err.message)
    } finally {
      setResubmitting(false)
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6">
      <h2 className="font-display text-lg font-bold text-paper-0 mb-5 flex items-center gap-2">
        {status === "rejected"
          ? <XCircle className="h-5 w-5 text-red-400" />
          : <Clock className="h-5 w-5 text-gold-400" />}
        {status === "rejected" ? t("profile_rejected_title") : t("profile_pending_title")}
      </h2>

      {status === "rejected" && (
        <form onSubmit={handleResubmit} className="mb-6 flex flex-col gap-3 border-b border-white/10 pb-6">
          <p className="text-xs font-medium text-mist-400">Choose a club and coach to resubmit your membership request.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <select
              value={clubId}
              onChange={(e) => { setClubId(e.target.value); setCoachId("") }}
              className="h-10 rounded-lg border border-white/15 bg-ink-700/60 px-3 text-sm text-paper-0"
            >
              <option value="">{t("profile_select_club")}</option>
              {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              value={coachId}
              onChange={(e) => setCoachId(e.target.value)}
              disabled={!clubId}
              className="h-10 rounded-lg border border-white/15 bg-ink-700/60 px-3 text-sm text-paper-0 disabled:opacity-50"
            >
              <option value="">{t("profile_select_coach")}</option>
              {coaches.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </div>
          {resubmitMsg && <p className="text-xs font-medium text-red-300">{resubmitMsg}</p>}
          <button
            type="submit"
            disabled={resubmitting}
            className="flex h-10 w-fit items-center gap-2 rounded-lg bg-gold-500 px-4 text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-400 disabled:opacity-60"
          >
            {resubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {t("profile_resubmit")}
          </button>
        </form>
      )}

      <form onSubmit={handleUpload} className="flex flex-col gap-4">
        <p className="text-xs font-medium text-mist-400">
          {bothCleared
            ? t("profile_docs_fine")
            : status === "rejected"
            ? t("profile_update_docs_rejected")
            : t("profile_update_docs_pending")}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {certCleared ? (
            <ClearedDocument
              icon={<FileImage className="h-3 w-3" />}
              label={t("profile_birth_cert")}
              note={birthCertStatus === "approved" ? t("profile_verified") : t("profile_submitted")}
              onView={toggleViewCert}
              viewing={viewOpen === "cert"}
              t={t}
            />
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mist-500">
                <FileImage className="h-3 w-3" /> {t("profile_birth_cert")}
              </span>
              {birthCertStatus === "rejected" && birthCertRejectReason && (
                <p className="text-xs font-medium text-red-300">Rejected: {birthCertRejectReason}</p>
              )}
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setCertFile(e.target.files[0] || null)}
                className="text-xs text-mist-300 file:mr-3 file:rounded-full file:border-0 file:bg-white/5 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-gold-400 hover:file:bg-white/10"
              />
            </label>
          )}

          {photoCleared ? (
            <ClearedDocument
              icon={<User className="h-3 w-3" />}
              label={t("profile_photo")}
              note={t("profile_uploaded")}
              onView={photoUrl && photoUrl !== "pending" ? () => setViewOpen(viewOpen === "photo" ? null : "photo") : undefined}
              viewing={viewOpen === "photo"}
              t={t}
            />
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mist-500">
                <User className="h-3 w-3" /> {t("profile_photo")}
              </span>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                className="text-xs text-mist-300 file:mr-3 file:rounded-full file:border-0 file:bg-white/5 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-gold-400 hover:file:bg-white/10"
              />
            </label>
          )}
        </div>

        {viewOpen === "cert" && (
          <div className="border-t border-white/10 pt-4">
            {certViewLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-gold-500" />
            ) : certViewError ? (
              <p className="text-sm font-medium text-red-300">{certViewError}</p>
            ) : certView ? (
              certView.isPdf ? (
                <iframe src={certView.src} title="Birth certificate" className="h-[420px] w-full border border-white/10 rounded" />
              ) : (
                <img src={certView.src} alt="Birth certificate" className="max-h-[420px] w-auto border border-white/10 rounded" />
              )
            ) : null}
          </div>
        )}

        {viewOpen === "photo" && photoUrl && photoUrl !== "pending" && (
          <div className="border-t border-white/10 pt-4">
            <img src={photoUrl} alt="Profile" className="max-h-[300px] w-auto rounded border border-white/10" />
          </div>
        )}

        {uploadMsg && <p className="text-xs font-medium text-mist-300">{uploadMsg}</p>}
        {!bothCleared && (
          <button
            type="submit"
            disabled={uploading || (!certFile && !photoFile)}
            className="flex h-10 w-fit items-center gap-2 rounded-lg border border-white/20 px-4 text-sm font-semibold text-paper-0 transition-colors hover:border-white/40 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {t("profile_save")}
          </button>
        )}
      </form>
    </div>
  )
}

function ClearedDocument({ icon, label, note, onView, viewing, t }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mist-500">
        {icon} {label}
      </span>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
          <Check className="h-3.5 w-3.5" /> {note}
        </div>
        {onView && (
          <button
            type="button"
            onClick={onView}
            className="text-xs font-semibold text-gold-400 hover:text-gold-300 underline underline-offset-2"
          >
            {viewing ? t("profile_hide") : t("profile_view")}
          </button>
        )}
      </div>
    </div>
  )
}


function InfoRow({ label, value, accent, mono, capitalize }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-mist-500 mb-1">{label}</p>
      <p className={`text-sm font-semibold break-words ${accent ? "text-gold-400" : mono ? "font-mono text-paper-0" : "text-paper-0"} ${capitalize ? "capitalize" : ""}`}>{value}</p>
    </div>
  )
}
